import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight } from 'lucide-react';
import NavBar from './NavBar';
import Footer from './Footer';

interface BlogPost {
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  icon: string;
  slug: string;
  date: string;
}

const scrollToSection = (id: string) => {
  if (window.location.pathname !== '/') {
    window.location.href = `/#${id}`;
  } else {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }
};

const BlogSection: React.FC = () => {
  const blogPosts: BlogPost[] = [
    {
      title: "Preparing for Pregnancy: How Chinese Medicine + Women's Health Nutrition Can Support Your Fertility",
      excerpt: "Dr. Kirby Quin and women's health dietitian Rachel Hazlett on acupuncture plus practical, personalised nutrition for your fertility journey.",
      category: "Fertility & Preconception",
      readTime: "8 min read",
      icon: "❤️",
      slug: "pregnancy-preparation-chinese-medicine-nutrition",
      date: "2025-08-08"
    },
    {
      title: "Winter Skin Health: Cosmetic Acupuncture + Botanical Skincare",
      excerpt: "Discover the inside-out approach to glowing skin using Traditional Chinese Medicine and award-winning Australian botanicals.",
      category: "Skin Health",
      readTime: "7 min read",
      icon: "✨",
      slug: "winter-skin-health",
      date: "2025-07-11"
    },
    {
      title: "Fertility Foods to Nourish Hormone Health",
      excerpt: "What to Eat (and What to Avoid) When Preparing for Pregnancy. Discover the top foods that support fertility and hormone balance naturally.",
      category: "Nutrition",
      readTime: "6 min read",
      icon: "🌿",
      slug: "fertility-foods-hormone-health",
      date: "2025-06-20"
    },
    {
      title: "The Critical 3-Month Preconception Window",
      excerpt: "Preparing Your Body for Conception and Pregnancy. Learn why the 3 months before conception play a crucial role in fertility outcomes.",
      category: "Fertility",
      readTime: "5 min read",
      icon: "🌱",
      slug: "preconception-window",
      date: "2025-06-17"
    },
    {
      title: "Seed Cycling for Hormonal Balance",
      excerpt: "A Simple Step Toward Fertility Wellness. Discover how eating specific seeds during different phases can support your menstrual cycle.",
      category: "Hormonal Health",
      readTime: "4 min read",
      icon: "🌿",
      slug: "seed-cycling-hormonal-balance",
      date: "2025-06-13"
    },
    {
      title: "Everyday Toxins That Can Impact Fertility",
      excerpt: "And What You Can Do About Them. Learn about common endocrine-disrupting chemicals and how to reduce your exposure.",
      category: "Environmental Health",
      readTime: "6 min read",
      icon: "🛡️",
      slug: "toxins-fertility",
      date: "2025-06-10"
    }
  ];

  const handleReadMore = (slug: string) => {
    window.location.href = `/blog/${slug}`;
  };

  const handleViewAllArticles = () => {
    window.location.href = '/blog';
  };

  React.useEffect(() => {
    // Fix anchor navigation from other pages
    const hash = window.location.hash;
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 200);
      }
    }
  }, []);

  // Check if this is being used as a standalone page
  const isStandalonePage = window.location.pathname === '/blog';

  // Sort posts by date descending
  const sortedPosts = [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  // Show only 3 latest on landing, all on /blog
  const postsToShow = isStandalonePage ? sortedPosts : sortedPosts.slice(0, 3);

  return (
    <>
      {isStandalonePage && <NavBar scrollToSection={scrollToSection} />}
      <section id="blog" className="section-padding bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-16">
            <h2 className="mb-4 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent font-serif text-3xl md:text-4xl font-semibold">Wellness Wisdom</h2>
            <p className="max-w-3xl mx-auto text-lg text-gray-700">
              Stay informed with evidence-based insights on women's health, fertility, and wellness.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {postsToShow.map((post, index) => (
              <Card
                key={index}
                className="overflow-hidden rounded-2xl shadow-lg border-2 border-amber-200/30 card-hover"
              >
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-4xl">{post.icon}</div>
                    <span className="text-sm text-sage-green font-medium">{post.readTime}</span>
                  </div>
                  <div className="mb-2 text-xs text-gray-500 font-medium">{new Date(post.date).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })}</div>
                  <div className="mb-4">
                    <span className="inline-block bg-soft-pink text-earth-brown text-xs font-medium px-3 py-1 rounded-full mb-3">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold mb-2 text-sage-800">{post.title}</h3>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  <Button 
                    variant="ghost" 
                    className="text-sage-green hover:text-sage-green/80 hover:bg-sage-green/10 p-0 h-auto font-medium"
                    onClick={() => handleReadMore(post.slug)}
                  >
                    Read More
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg" 
              className="bg-sage-600 hover:bg-sage-700 text-white font-medium text-base px-8 py-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
              onClick={handleViewAllArticles}
            >
              {isStandalonePage ? 'Back to Home' : 'Explore More Blogs'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>
      {/* Removed duplicate Footer here; App.tsx handles the global footer */}
    </>
  );
};

export default BlogSection;